// Subpath imports, never the '@onark/shared' barrel: the barrel re-exports
// ./queue → bullmq → ioredis, which breaks the Next browser bundle.
import { IntegrationSource } from '@onark/shared/integrationSource';
import { DEFAULT_TEAM_SUGGESTION_OPTIONS } from '@onark/shared/teamSelection';

const defaultOptions = () => [
  {
    id: `_${Math.random().toString(36).substring(2, 11)}`,
    label: 'Option 1',
    order: 1,
  },
];

export const sectionTypes: TypeButtonGroup<
  Omit<SectionCreateInput, 'course'>
>[] = [
  {
    buttons: [
      {
        defaultObj: {
          name: 'Untitled Video Section',
          type: 'Video',
        },
        hint: 'Embed a YouTube video into your class and add questions while it plays',
        icon: 'SECTIONS_Video',
        label: 'Video',
      },
      {
        defaultObj: {
          name: 'Untitled Form Section',
          type: 'Form',
        },
        hint: 'Add any inputs you want to collect information from your guests',
        icon: 'SECTIONS_Form',
        label: 'Form',
      },
      {
        defaultObj: {
          name: 'Untitled Test Section',
          type: 'Test',
        },
        hint: 'Add the DISC, Enneagram or Spiritual Gifts Test to your class',
        icon: 'SECTIONS_Test',
        label: 'Test',
      },
      {
        defaultObj: {
          name: 'Untitled Text Section',
          type: 'Text',
        },
        hint: 'Basic section that lets you add rich text',
        icon: 'SECTIONS_Text',
        label: 'Text',
      },
      {
        defaultObj: {
          name: 'Untitled Branch Section',
          type: 'Branch',
        },
        hint: 'Give your guests the ability to choose their own path',
        icon: 'SECTIONS_Branch',
        label: 'Branch',
      },
    ],
    label: 'Section Types',
  },
];

export const branchSectionTypes: TypeButtonGroup<{
  subSection: Partial<SubSectionCreateInput>;
  section?: Omit<SectionCreateInput, 'course'>;
}>[] = [
  {
    buttons: [
      {
        defaultObj: {
          subSection: {
            label: 'Continue',
            text: '',
          },
        },
        icon: 'ADMIN_NAV_Classes',
        label: 'Next Section',
      },
      {
        defaultObj: {
          section: {
            name: 'Untitled Form Section',
            type: 'Form',
          },
          subSection: {
            label: 'Go To Form',
            text: '',
          },
        },
        icon: 'SECTIONS_Form',
        label: 'Form',
      },
      {
        defaultObj: {
          section: {
            name: 'Untitled Test Section',
            type: 'Test',
          },
          subSection: {
            label: 'Go To Test',
            text: '',
          },
        },
        icon: 'SECTIONS_Test',
        label: 'Test',
      },
      {
        defaultObj: {
          section: {
            name: 'Untitled Text Section',
            type: 'Text',
          },
          subSection: {
            label: 'Go To Text',
            text: '',
          },
        },
        icon: 'SECTIONS_Text',
        label: 'Text',
      },
      {
        defaultObj: {
          section: {
            name: 'Untitled Video Section',
            type: 'Video',
          },
          subSection: {
            label: 'Go To Video',
            text: '',
          },
        },
        icon: 'SECTIONS_Video',
        label: 'Video',
      },
    ],
    label: 'Branch Type',
  },
];
export const inputTypes: TypeButtonGroup<{
  options?: FormOption[] | TeamSuggestionOptions;
  placeholder: string;
  required: boolean;
  type: InputType;
}>[] = [
  {
    buttons: [
      {
        defaultObj: {
          placeholder: 'Short Text',
          required: true,
          type: 'ShortText',
        },
        icon: 'INPUTS_ShortText',
        label: 'Short Text',
      },
      {
        defaultObj: {
          placeholder: 'Paragraph',
          required: true,
          type: 'Paragraph',
        },
        icon: 'INPUTS_Paragraph',
        label: 'Paragraph',
      },
      {
        defaultObj: {
          placeholder: 'Display Text',
          required: true,
          type: 'DisplayText',
        },
        icon: 'INPUTS_DisplayText',
        label: 'Display Text',
      },
    ],
    label: 'Text',
  },
  {
    buttons: [
      {
        defaultObj: {
          options: defaultOptions(),
          placeholder: 'Checkbox',
          required: true,
          type: 'Checkbox',
        },
        icon: 'INPUTS_Checkbox',
        label: 'Checkbox',
      },
      {
        defaultObj: {
          options: defaultOptions(),
          placeholder: 'Multiple Choice',
          required: true,
          type: 'Radio',
        },
        icon: 'INPUTS_Radio',
        label: 'Multiple Choice',
      },
      {
        defaultObj: {
          options: defaultOptions(),
          placeholder: 'Dropdown',
          required: true,
          type: 'Dropdown',
        },
        icon: 'INPUTS_Dropdown',
        label: 'Dropdown Menu',
      },
      {
        defaultObj: {
          options: { ...DEFAULT_TEAM_SUGGESTION_OPTIONS },
          placeholder: 'Ministries',
          required: true,
          type: 'TeamSuggestion',
        },
        icon: 'INPUTS_TeamSuggestion',
        label: 'Ministries',
      },
    ],
    label: 'Selection',
  },
  {
    buttons: [
      {
        defaultObj: {
          options: [],
          placeholder: 'User Download',
          required: true,
          type: 'UserDownload',
        },
        icon: 'INPUTS_UserDownload',
        label: 'User Download',
      },
      {
        defaultObj: {
          placeholder: 'User Upload',
          required: true,
          type: 'UserUpload',
        },
        icon: 'INPUTS_UserUpload',
        label: 'User Upload',
      },
    ],
    label: 'Files',
  },
  {
    buttons: [
      {
        defaultObj: {
          placeholder: 'Date',
          required: true,
          type: 'Date',
        },
        icon: 'INPUTS_Date',
        label: 'Date',
      },
      {
        defaultObj: {
          placeholder: 'Time',
          required: true,
          type: 'Time',
        },
        icon: 'INPUTS_Time',
        label: 'Time',
      },
    ],
    label: 'Date/Time',
  },
];

// The guest-contact merge fields every email automation can address. A fresh
// array per call, because the Checkin builder pushes onto its copy.
export const guestContactValues = () => [
  { field: 'firstName', id: 'GUEST_UUID', label: 'First Name', model: 'guest' },
  { field: 'lastName', id: 'GUEST_UUID', label: 'Last Name', model: 'guest' },
  { field: 'email', id: 'GUEST_UUID', label: 'Email', model: 'guest' },
  { field: 'phone', id: 'GUEST_UUID', label: 'Phone', model: 'guest' },
];

// `integrationSource` is a bare `string` on the generated input type, because
// the Postgres column is still `text`. Narrowing it to the shared union makes
// a typo in the three literals below a compile error instead of an automation
// whose job name no worker handler matches.
export const automationTypes: (TypeButtonGroup<
  Omit<AutomationCreateInput, 'section' | 'integrationSource'> & {
    integrationSource: IntegrationSource;
  }
> & { source: 'PCO' | 'Default' })[] = [
  {
    buttons: [
      {
        defaultObj: {
          integrationSource: 'Email',
          label: 'Send Email To...',
          type: 'Email',
          values: guestContactValues(),
        },
        icon: 'AUTOMATIONS_Email',
        label: 'Send Email',
      },
    ],
    label: 'Default Integrations',
    source: 'Default',
  },
  {
    buttons: [
      {
        defaultObj: {
          integrationSource: 'PCO',
          label: 'Save to PCO Field...',
          type: 'PCOField',
        },
        icon: 'AUTOMATIONS_PCOField',
        label: 'Save To Profile Field',
      },
      {
        defaultObj: {
          integrationSource: 'PCO',
          label: 'Add to PCO Workflow...',
          type: 'PCOWorkflow',
        },
        icon: 'AUTOMATIONS_PCOWorkflow',
        label: 'Add to Workflow',
      },
    ],

    label: 'Planning Center',
    source: 'PCO',
  },
];
